/**
 * connection-data-card.tsx — "Dữ liệu từ các kết nối": hồ sơ khách ĐỘNG theo từng nguồn đồng bộ.
 * Nguồn nào (Evotech, Pancake, ứng dụng ngoài…) đã gửi dữ liệu về thì có CỤM của nó; không có dữ liệu thì không hiện.
 * Lọc theo kết nối, sắp xếp cụm, thu gọn từng cụm — lựa chọn nhớ trên trình duyệt này.
 */
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { ChevronDown, ChevronRight, Database } from 'lucide-react'
import { cn } from '@/lib/utils'
import { CLUSTER_SORT_LABELS, formatAttrValue, groupAttrs, sortClusters, type ClusterSort, type ConnAttr, type ConnCluster } from '@/lib/attr-format'
import { useContactAttributes } from '@/hooks/use-contact-overview'
import { safeHttpUrl } from '@/lib/safe-url'
import { useTranslation } from 'react-i18next'

const PREF_KEY = 'ecdp_conn_data_prefs'
interface Prefs { sort: ClusterSort; collapsed: Record<string, boolean> }

function loadPrefs(): Prefs {
  try {
    const p = JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}') as Partial<Prefs>
    return { sort: p.sort && p.sort in CLUSTER_SORT_LABELS ? p.sort : 'connection', collapsed: p.collapsed ?? {} }
  } catch {
    return { sort: 'connection', collapsed: {} }
  }
}

function Value({ a }: { a: ConnAttr }) {
  const text = formatAttrValue(a)
  if (a.valueType === 'url') {
    const href = safeHttpUrl(a.value)
    return href ? (
      <a href={href} target="_blank" rel="noreferrer" className="break-all text-primary hover:underline">{text}</a>
    ) : (
      <span className="break-words font-medium">{text}</span>
    )
  }
  return <span className="break-words font-medium">{text}</span>
}

function Cluster({ cluster, collapsed, onToggle }: { cluster: ConnCluster; collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation('custDetail')
  return (
    <div className="rounded-lg border">
      <button type="button" onClick={onToggle} aria-expanded={!collapsed} className="flex w-full items-center gap-2 px-3 py-2 text-left">
        {collapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        <span className="text-sm font-medium">{cluster.label}</span>
        <span className="rounded bg-muted px-1.5 text-[11px] text-muted-foreground">{t('conn.fieldCount', { count: cluster.attributes.length })}</span>
        <span className="ml-auto text-[11px] text-muted-foreground">{t('conn.synced', { time: dayjs(cluster.syncedAt).format('HH:mm DD/MM/YYYY') })}</span>
      </button>
      {!collapsed && (
        <div className="space-y-3 border-t px-3 py-2.5">
          {groupAttrs(cluster.attributes).map((g) => (
            <div key={g.title ?? 'all'}>
              {g.title && <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{g.title}</p>}
              <dl>
                {g.items.map((a) => (
                  <div key={a.key} className="grid grid-cols-[130px_1fr] gap-2 py-0.5 text-sm" title={a.sourceAt ? t('conn.originated', { time: dayjs(a.sourceAt).format('HH:mm DD/MM/YYYY') }) : undefined}>
                    <dt className="text-muted-foreground">{a.label}</dt>
                    <dd className="min-w-0"><Value a={a} /></dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function ConnectionDataCard({ contactId }: { contactId: string }) {
  const { t } = useTranslation('custDetail')
  const { data, isLoading } = useContactAttributes(contactId)
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs)
  const [only, setOnly] = useState<string | null>(null)
  useEffect(() => {
    try { localStorage.setItem(PREF_KEY, JSON.stringify(prefs)) } catch { /* trình duyệt chặn lưu → bỏ qua */ }
  }, [prefs])

  const clusters = data?.clusters ?? []
  const shown = sortClusters(only ? clusters.filter((c) => c.source === only) : clusters, prefs.sort)
  const toggle = (src: string) => setPrefs((p) => ({ ...p, collapsed: { ...p.collapsed, [src]: !p.collapsed[src] } }))

  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex items-center gap-2 font-medium"><Database className="h-4 w-4" /> {t('conn.title')}</h3>
        {clusters.length > 1 && (
          <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
            {t('conn.sort')}
            <select value={prefs.sort} onChange={(e) => setPrefs((p) => ({ ...p, sort: e.target.value as ClusterSort }))} className="rounded-md border bg-background px-1.5 py-1 text-xs text-foreground">
              {(Object.keys(CLUSTER_SORT_LABELS) as ClusterSort[]).map((k) => <option key={k} value={k}>{CLUSTER_SORT_LABELS[k]}</option>)}
            </select>
          </label>
        )}
      </div>
      {isLoading ? (
        <div className="mt-3 h-16 animate-pulse rounded-md bg-muted/40" />
      ) : clusters.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">{t('conn.empty')}</p>
      ) : (
        <>
          {clusters.length > 1 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {[{ source: null as string | null, label: t('conn.all'), n: clusters.reduce((s, c) => s + c.attributes.length, 0) }, ...clusters.map((c) => ({ source: c.source as string | null, label: c.label, n: c.attributes.length }))].map((c) => (
                <button key={c.source ?? 'all'} type="button" onClick={() => setOnly(c.source)} className={cn('rounded-full border px-2.5 py-0.5 text-xs', only === c.source ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}>
                  {c.label} <span className="opacity-70">{c.n}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-3 space-y-2.5">
            {shown.map((c) => <Cluster key={c.source} cluster={c} collapsed={!!prefs.collapsed[c.source]} onToggle={() => toggle(c.source)} />)}
          </div>
        </>
      )}
    </div>
  )
}
